import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Fab,
  IconButton,
  InputBase,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile,
  Close,
  CloseFullscreen,
  DeleteSweep,
  Lock,
  OpenInFull,
  Send,
  Stop,
} from '@mui/icons-material';
import { alpha, Theme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import { KarlDino } from './KarlDino';

import {
  IKiAttachment,
  IKiMessage,
  KI_ATTACHMENT_TYPES,
} from '../Interfaces/IKi';
import { fileToAttachment, validateAttachmentFile } from './kiAttachment';
import {
  useAskKi,
  useGetKiStatus,
  useKiAbbrechen,
} from '../hooks/reactQuerys/KiQueries';
import { KiMarkdown } from './KiMarkdown';
import { KiTaskProposalCard } from './KiTaskProposalCard';
import { KiGrundbuchProposalCard } from './KiGrundbuchProposalCard';
import { KiMandantProposalCard } from './KiMandantProposalCard';
import { KiAusweiseProposalCard } from './KiAusweiseProposalCard';
import { KiDownloadProposalCard } from './KiDownloadProposalCard';
import { KiRegisterAbrufProposalCard } from './KiRegisterAbrufProposalCard';
import { KiAblageProposalCard } from './KiAblageProposalCard';
import { KiDatevExportProposalCard } from './KiDatevExportProposalCard';
import { KiVorgangProposalCard } from './KiVorgangProposalCard';
import { KiVorgangStatusProposalCard } from './KiVorgangStatusProposalCard';
import {
  KiKontrollstrukturAblageProposalCard,
  KiKontrollstrukturProposalCard,
} from './KiKontrollstrukturProposalCard';
import { KiVorgangContext, vorgangAusPfad } from './KiVorgangContext';
import { KiInternetFreigabeCard, KiInternetHinweis } from './KiInternet';
import { IKiBeispiel, KiBeispiele } from './KiBeispiele';

/** Verlauf überlebt das Schließen des Fensters, nicht aber die Abmeldung. */
const STORAGE_KEY = 'dino.ki.chat';

/** So viele Nachrichten gehen als Verlauf mit — der Server kürzt ebenfalls. */
const MAX_HISTORY = 40;

/**
 * Die Werkzeuge in der Sprache der Oberfläche. Wer sieht, worin die KI
 * nachgesehen hat, kann die Antwort einschätzen — und im Prod-Testing fällt
 * auf, wenn ein Modell gar nicht erst nachschlägt.
 */
const TOOL_LABEL: Record<string, string> = {
  meine_massnahmen: 'Meine Maßnahmen',
  meine_vorgaenge: 'Meine Vorgänge',
  meine_aufgaben: 'Meine Aufgaben',
  meine_termine: 'Meine Termine',
  vorgang_suche: 'Vorgangssuche',
  vorgang_details: 'Vorgangsdetails',
  report_liste: 'Auswertungen',
  report_ausfuehren: 'Auswertung',
  protokoll_suche: 'Protokoll',
  mitarbeiter_suche: 'Mitarbeitersuche',
  aufgaben_stammdaten: 'Aufgaben-Stammdaten',
  aufgabe_vorschlagen: 'Aufgabe vorbereitet',
  grundbuch_vorschlagen: 'Grundbuchauszug ausgelesen',
  internet_suche: 'Internet',
};

const readStored = (): IKiMessage[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as IKiMessage[]) : [];
  } catch {
    // Privater Modus oder voller Speicher — dann eben ohne Verlauf.
    return [];
  }
};

/**
 * Chat-Fenster der KI-Anbindung (#1336).
 *
 * Unten rechts über allen Seiten, nach dem Vorbild der Karl-Fassung aus dem
 * Zweig 1243 — ohne die Anonymisierung: was hier steht, geht unverändert an
 * den eingerichteten Anbieter. Der Hinweis dazu steht im Fenster, damit er
 * dort sichtbar ist, wo getippt wird, und nicht nur in den Einstellungen.
 *
 * Das Fenster blendet sich vollständig aus, solange der Administrator die KI
 * nicht eingeschaltet **und** einen Anbieter mit Schlüssel eingerichtet hat.
 * Ein Knopf, der nur eine Fehlermeldung öffnet, wäre schlechter als keiner.
 */
/** Karls Zeichen: ein Farbverlauf-Kreis mit Funkeln. */
const verlauf = (theme: Theme) =>
  `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

const KarlAvatar: React.FC<{ groesse?: number; modus?: 'ruhe' | 'denkt' }> = ({
  groesse = 30,
  modus = 'ruhe',
}) => (
  <Box
    sx={{
      flexShrink: 0,
      width: groesse,
      height: groesse,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      color: 'common.white',
      background: verlauf,
      boxShadow: theme =>
        `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
    }}
  >
    <KarlDino groesse={groesse * 0.52} modus={modus} />
  </Box>
);

const puls = keyframes`
  0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;

/** „Karl denkt nach" — drei Punkte statt eines Kreisels. */
const Denkpunkte: React.FC = () => (
  <Stack direction='row' spacing={0.5} alignItems='center' sx={{ px: 0.5 }}>
    {[0, 1, 2].map(i => (
      <Box
        key={`punkt-${i}`}
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          animation: `${puls} 1.2s ease-in-out ${i * 0.15}s infinite`,
        }}
      />
    ))}
  </Stack>
);

export const KiChatWidget: React.FC = () => {
  const { kiStatus } = useGetKiStatus();
  const { askKi, isPendingAskKi } = useAskKi();
  const { meldeAbbruch } = useKiAbbrechen();
  // Der Vorgang, auf dessen Seite der Chat offen ist — Karl soll „dieser
  // Vorgang" verstehen, und Karten fragen, ob sie dort ablegen sollen.
  const idVorgang = vorgangAusPfad(useLocation().pathname);

  const [isOpen, setIsOpen] = useState(false);
  /** Vergroessert auf nahezu die ganze Seite — fuer lange Antworten. */
  const [isExpanded, setIsExpanded] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<IKiMessage[]>(readStored);
  const [error, setError] = useState<string | null>(null);
  /**
   * Die Datei des Gesprächs (#1336).
   *
   * Sie bleibt hängen, bis der Benutzer sie entfernt oder eine andere wählt,
   * und geht mit **jeder** Frage erneut mit. Vorher wurde sie nach dem
   * Absenden verworfen: das Werkzeug zur Umwandlung wird nur angeboten, wenn
   * eine PDF an genau dieser Frage hängt, und so stand das Modell bei der
   * zweiten Frage ohne Datei und ohne Werkzeug da — es antwortete dann, es
   * gebe diese Funktion in DiNo gar nicht. Wer eine Datei in den Chat zieht,
   * erwartet, dass sie im Gespräch bleibt.
   */
  const [attachment, setAttachment] = useState<IKiAttachment | null>(null);
  /**
   * Ob die Datei in diesem Zug neu dazukam. Nur dann steht sie beim eigenen
   * Beitrag im Verlauf — sonst hinge an jeder weiteren Frage dieselbe
   * Büroklammer, obwohl der Benutzer nichts Neues angehängt hat.
   */
  const [isAttachmentNew, setIsAttachmentNew] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const eingabeRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  /** Die laufende Frage, damit sie sich abbrechen lässt. */
  const abortRef = useRef<AbortController | null>(null);
  /** Die Kennung der laufenden Frage — über sie entwertet DiNo das Token. */
  const abbruchKennungRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      // Vorschläge bleiben draußen: ein Vorschlag, der nach einem Neuladen
      // wieder anklickbar wäre, legt eine Aufgabe an, die zu einem Gespräch
      // gehört, an das sich niemand mehr erinnert.
      const persistable = messages.map(
        ({ proposals: _proposals, ...entry }) => entry
      );
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    } catch {
      /* Speicher nicht verfügbar — der Verlauf bleibt dann nur im Fenster. */
    }
  }, [messages]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isPendingAskKi]);

  if (!kiStatus?.Ready) {
    return null;
  }

  /**
   * Eine hineingezogene, eingefügte oder ausgewählte Datei übernehmen.
   *
   * Geprüft wird hier **und** auf dem Server. Hier, damit der Benutzer die
   * Meldung sofort sieht und nicht erst nach dem Hochladen von zehn Megabyte;
   * dort, weil eine Prüfung im Browser keine Prüfung ist.
   */
  async function handleFiles(files: FileList | null) {
    const file = files?.[0];

    if (file === undefined) {
      return;
    }

    const problem = validateAttachmentFile(file);

    if (problem !== null) {
      setError(problem);

      return;
    }

    setError(null);

    try {
      setAttachment(await fileToAttachment(file));
      setIsAttachmentNew(true);
    } catch {
      setError('Die Datei konnte nicht gelesen werden.');
    }
  }

  /**
   * Ein Beispiel übernehmen: in das Eingabefeld, nicht abschicken — der
   * Platzhalter („FIRMA") ist markiert und wird beim Tippen ersetzt. Braucht
   * das Beispiel eine Datei, öffnet sich gleich die Auswahl.
   */
  function waehleBeispiel(beispiel: IKiBeispiel) {
    setInput(beispiel.text);
    if (beispiel.datei && attachment === null) {
      fileInputRef.current?.click();
    }
    window.setTimeout(() => {
      const feld = eingabeRef.current;
      if (feld === null) {
        return;
      }
      feld.focus();
      const start = beispiel.platzhalter
        ? beispiel.text.indexOf(beispiel.platzhalter)
        : -1;
      if (start >= 0 && beispiel.platzhalter) {
        feld.setSelectionRange(start, start + beispiel.platzhalter.length);
      } else {
        feld.setSelectionRange(beispiel.text.length, beispiel.text.length);
      }
    }, 0);
  }

  /** `text`: eine Nachricht, die nicht aus dem Eingabefeld kommt — etwa die
   *  Bestätigung nach der Freigabe einer Internetsuche. */
  async function handleSend(text?: string) {
    const message = (text ?? input).trim();

    if (message === '' || isPendingAskKi) {
      return;
    }

    setError(null);

    // Den Verlauf *vor* der eigenen Nachricht mitschicken: der Server hängt
    // die Frage selbst an, sonst stünde sie zweimal im Gespräch.
    // Nur Rolle und Text gehen mit: die Werkzeugnamen sind Anzeige, kein
    // Gesprächsinhalt, und die Schnittstelle nimmt nur diese beiden Felder.
    const history = messages
      .slice(-MAX_HISTORY)
      .map(entry => ({ role: entry.role, content: entry.content }));

    const sent = attachment;
    const wasNew = isAttachmentNew;

    setMessages(previous => [
      ...previous,
      {
        role: 'user',
        content:
          sent !== null && wasNew ? `📎 ${sent.FileName}\n${message}` : message,
      },
    ]);
    if (text === undefined) {
      setInput('');
    }
    // Die Datei bleibt; nur „neu" ist sie ab jetzt nicht mehr.
    setIsAttachmentNew(false);

    const controller = new AbortController();
    abortRef.current = controller;
    // Nur für diese eine Anfrage. DiNo hängt sie an das Werkzeug-Token; beim
    // Abbruch wird es darüber entwertet.
    const abbruchKennung =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    abbruchKennungRef.current = abbruchKennung;

    try {
      const answer = await askKi({
        Message: message,
        History: history,
        ...(sent !== null ? { Attachment: sent } : {}),
        idLegalTransaction: idVorgang,
        signal: controller.signal,
      });
      const vorschlaege = answer.ProposalArray || [];
      // Die Internetsuche ist kein Vorschlag, sondern eine Eigenschaft der
      // Antwort: sie wandert an die Nachricht und bleibt dort sichtbar.
      const internet = vorschlaege.flatMap(proposal =>
        proposal.Kind === 'internet_quellen' ? [proposal.Display] : []
      );
      setMessages(previous => [
        ...previous,
        {
          role: 'assistant',
          content: answer.Text,
          tools: (answer.ToolCallArray || []).map(call => call.name),
          ...(internet.length > 0 ? { internet } : {}),
          // Eine Ablage braucht die Datei, und die kommt nicht vom Server
          // zurück: es ist die, die mit genau dieser Frage ging.
          proposals: vorschlaege
            .filter(proposal => proposal.Kind !== 'internet_quellen')
            .map(proposal =>
              proposal.Kind === 'ablage' && sent !== null
                ? { ...proposal, Datei: sent }
                : proposal
            ),
        },
      ]);
    } catch (exception) {
      if (controller.signal.aborted) {
        // Abgebrochen heißt: als wäre die Frage nie abgeschickt worden. Text
        // und Datei kommen zurück ins Eingabefeld, die Frage verschwindet aus
        // dem Verlauf. Der häufigste Grund für einen Abbruch ist ein Vertipper
        // — wer dann neu tippen müsste, hätte nichts gewonnen. Und eine Frage,
        // die unbeantwortet im Verlauf stünde, ginge beim nächsten Mal als
        // Zusammenhang mit an das Modell.
        setMessages(previous => previous.slice(0, -1));
        setInput(message);
        setAttachment(sent);
        setIsAttachmentNew(wasNew);

        return;
      }

      // Die Meldung des Servers nennt den Grund (kein Schlüssel, Anbieter
      // nicht erreichbar, Modell unbekannt) — die gehört dem Benutzer gezeigt.
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';

      setError(
        detail !== ''
          ? detail
          : 'Die Antwort konnte nicht abgerufen werden. Bitte erneut versuchen.'
      );
    } finally {
      abortRef.current = null;
    }
  }

  return (
    <KiVorgangContext.Provider value={idVorgang}>
      {isOpen && (
        <Paper
          elevation={8}
          onDragOver={(event: React.DragEvent) => {
            // Ohne preventDefault öffnet der Browser die Datei in einem neuen
            // Tab, statt sie hier fallen zu lassen.
            event.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={(event: React.DragEvent) => {
            event.preventDefault();
            setIsDragOver(false);
            void handleFiles(event.dataTransfer.files);
          }}
          sx={{
            position: 'fixed',
            bottom: isExpanded ? 24 : 96,
            right: 24,
            // Deutlich groesser als der erste Entwurf: die Antworten sind
            // Fliesstext mit Aufzaehlungen, und in 420 x 520 Pixeln liest man
            // sie durch ein Schluesselloch.
            width: isExpanded
              ? 'min(1100px, calc(100vw - 48px))'
              : {
                  xs: 'calc(100vw - 48px)',
                  sm: 'min(640px, calc(100vw - 48px))',
                },
            height: isExpanded
              ? 'calc(100vh - 48px)'
              : 'min(680px, calc(100vh - 140px))',
            display: 'flex',
            flexDirection: 'column',
            zIndex: theme => theme.zIndex.drawer + 2,
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: theme =>
              `0 18px 50px ${alpha(theme.palette.common.black, 0.25)}`,
            // Beim Ziehen sichtbar werden: sonst rät man, ob das Fenster die
            // Datei überhaupt annimmt.
            outline: isDragOver ? '2px dashed' : 'none',
            outlineColor: 'primary.main',
            outlineOffset: -4,
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            spacing={1}
            sx={{
              px: 2,
              py: 1.25,
              background: verlauf,
              color: 'common.white',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: theme => alpha(theme.palette.common.white, 0.18),
                border: theme =>
                  `1px solid ${alpha(theme.palette.common.white, 0.35)}`,
              }}
            >
              <KarlDino groesse={18} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction='row' spacing={0.75} alignItems='center'>
                <Typography
                  variant='subtitle1'
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  Karl
                </Typography>
                <Box
                  title='bereit'
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#4caf50',
                    boxShadow: '0 0 0 2px rgba(255,255,255,0.6)',
                  }}
                />
              </Stack>
              <Typography
                variant='caption'
                sx={{ opacity: 0.85 }}
                noWrap
                component='div'
              >
                KI-Assistent · {kiStatus.ProviderLabel ?? kiStatus.Provider}
                {kiStatus.Model ? ` · ${kiStatus.Model}` : ''}
              </Typography>
            </Box>
            <Tooltip title={isExpanded ? 'Verkleinern' : 'Vergrößern'}>
              <IconButton
                size='small'
                sx={{ color: 'common.white' }}
                onClick={() => setIsExpanded(expanded => !expanded)}
              >
                {isExpanded ? (
                  <CloseFullscreen fontSize='small' />
                ) : (
                  <OpenInFull fontSize='small' />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title='Verlauf löschen'>
              <span>
                <IconButton
                  size='small'
                  sx={{ color: 'common.white' }}
                  disabled={messages.length === 0}
                  onClick={() => {
                    setMessages([]);
                    // Die Datei gehoert zum Gespraech: mit dem Verlauf geht
                    // auch sie, sonst haenge sie an einem Gespraech, das es
                    // nicht mehr gibt.
                    setAttachment(null);
                    setIsAttachmentNew(false);
                  }}
                >
                  <DeleteSweep fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton
              size='small'
              sx={{ color: 'common.white' }}
              onClick={() => setIsOpen(false)}
            >
              <Close fontSize='small' />
            </IconButton>
          </Stack>

          <Box
            ref={scrollRef}
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: theme =>
                theme.palette.mode === 'dark'
                  ? 'background.default'
                  : alpha(theme.palette.primary.main, 0.025),
            }}
          >
            {messages.length === 0 && (
              <Stack alignItems='center' spacing={1} sx={{ pt: 2, pb: 2.5 }}>
                <Box sx={{ color: 'text.primary', pb: 0.5 }}>
                  <KarlDino groesse={64} titel='Karl' />
                </Box>
                <Typography variant='h6' sx={{ fontWeight: 700, pt: 0.5 }}>
                  Hallo, ich bin Karl.
                </Typography>
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ textAlign: 'center', maxWidth: 420 }}
                >
                  Ihr KI-Assistent in DiNo. Ich sehe in Vorgängen, Maßnahmen und
                  Terminen nach, rufe das Handelsregister ab und bereite
                  Dokumente vor. Womit fange ich an?
                </Typography>
              </Stack>
            )}

            {messages.length === 0 && kiStatus.CanLookUp === false && (
              // Ohne diesen Hinweis wirkt es wie ein Fehler, wenn auf „welche
              // Maßnahmen habe ich?" eine Rückfrage statt einer Antwort kommt.
              //
              // `CanLookUp` statt `SupportsTools`: Letzteres heißt „DiNo kann
              // dem Anbieter Werkzeuge mitgeben" und ist bei watsonx
              // dauerhaft falsch — dessen Agent bringt eigene mit. Der Satz
              // stimmte also technisch und täuschte trotzdem.
              <Alert severity='warning' sx={{ mb: 2 }}>
                Dieses Modell kann <b>nicht in DiNo nachsehen</b>. Fragen zu
                Ihren Vorgängen, Maßnahmen oder Terminen kann es deshalb nicht
                beantworten.
              </Alert>
            )}

            {messages.length === 0 && (
              <>
                <KiBeispiele
                  status={kiStatus}
                  anbieter={kiStatus.ProviderLabel}
                  onWahl={waehleBeispiel}
                />
                <Stack
                  direction='row'
                  spacing={0.75}
                  alignItems='center'
                  justifyContent='center'
                  sx={{ mt: 2.5, color: 'text.secondary' }}
                >
                  <Lock sx={{ fontSize: 14 }} />
                  <Typography variant='caption' sx={{ textAlign: 'center' }}>
                    Ihre Eingaben gehen <b>unverändert</b> an{' '}
                    {kiStatus.ProviderLabel ?? 'den Anbieter'}. Bitte nur
                    eingeben, was dorthin gelangen darf.
                  </Typography>
                </Stack>
              </>
            )}

            <Stack spacing={1.5}>
              {messages.map((entry, index) => (
                <Stack
                  key={`ki-${index}-${entry.role}`}
                  direction='row'
                  spacing={1}
                  alignItems='flex-start'
                  sx={{
                    alignSelf:
                      entry.role === 'user' ? 'flex-end' : 'flex-start',
                    // Antworten duerfen fast die volle Breite nehmen, Fragen
                    // bleiben schmaler — dann sieht man auf einen Blick, wer
                    // spricht, ohne dass der Fliesstext umbricht.
                    maxWidth: entry.role === 'user' ? '85%' : '100%',
                  }}
                >
                  {entry.role === 'assistant' && <KarlAvatar />}
                  <Box
                    sx={{
                      minWidth: 0,
                      px: 1.75,
                      py: 1.1,
                      borderRadius: 3,
                      ...(entry.role === 'user'
                        ? {
                            borderBottomRightRadius: 6,
                            background: verlauf,
                            color: 'common.white',
                            boxShadow: (theme: Theme) =>
                              `0 3px 10px ${alpha(theme.palette.primary.main, 0.25)}`,
                          }
                        : {
                            borderTopLeftRadius: 6,
                            bgcolor: 'background.paper',
                            color: 'text.primary',
                            border: 1,
                            borderColor: 'divider',
                            boxShadow: (theme: Theme) =>
                              `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
                          }),
                    }}
                  >
                    {entry.role === 'assistant' ? (
                      <>
                        {entry.internet !== undefined &&
                          entry.internet.length > 0 && (
                            <KiInternetHinweis verwendung={entry.internet} />
                          )}
                        <KiMarkdown text={entry.content} />
                        {entry.tools !== undefined &&
                          entry.tools.length > 0 && (
                            <Typography
                              variant='caption'
                              sx={{
                                display: 'block',
                                mt: 1,
                                color: 'text.secondary',
                              }}
                            >
                              Nachgesehen in:{' '}
                              {Array.from(new Set(entry.tools))
                                .map(name => TOOL_LABEL[name] || name)
                                .join(', ')}
                            </Typography>
                          )}
                      </>
                    ) : (
                      <Typography
                        variant='body2'
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {entry.content}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              ))}

              {/* Vorschläge der letzten Antwort. Bewusst außerhalb der
                  Sprechblase und nur für den jüngsten Zug: eine Karte weiter
                  oben im Verlauf gehört zu einem Stand, der inzwischen
                  überholt sein kann. */}
              {(messages[messages.length - 1]?.proposals ?? []).map(
                (proposal, index) => {
                  const key = `ki-proposal-${messages.length}-${index}`;

                  // Bewusst ein switch: kommt eine Vorschlagsart dazu, zwingt
                  // die Union in IKi.ts diese Stelle, sie zu behandeln.
                  switch (proposal.Kind) {
                    case 'task':
                      return (
                        <KiTaskProposalCard key={key} proposal={proposal} />
                      );
                    case 'grundbuchblatt':
                      return (
                        <KiGrundbuchProposalCard
                          key={key}
                          proposal={proposal}
                        />
                      );
                    case 'mandant':
                      return (
                        <KiMandantProposalCard key={key} proposal={proposal} />
                      );
                    case 'ausweise':
                      return (
                        <KiAusweiseProposalCard key={key} proposal={proposal} />
                      );
                    case 'download':
                      return (
                        <KiDownloadProposalCard key={key} proposal={proposal} />
                      );
                    case 'registerabruf':
                      return (
                        <KiRegisterAbrufProposalCard
                          key={key}
                          proposal={proposal}
                        />
                      );
                    case 'ablage':
                      return (
                        <KiAblageProposalCard key={key} proposal={proposal} />
                      );
                    case 'vorgang':
                      return (
                        <KiVorgangProposalCard key={key} proposal={proposal} />
                      );
                    case 'vorgangstatus':
                      return (
                        <KiVorgangStatusProposalCard
                          key={key}
                          proposal={proposal}
                        />
                      );
                    case 'datevexport':
                      return (
                        <KiDatevExportProposalCard
                          key={key}
                          proposal={proposal}
                        />
                      );
                    case 'kontrollstruktur':
                      return (
                        <KiKontrollstrukturProposalCard
                          key={key}
                          proposal={proposal}
                        />
                      );
                    case 'internet_freigabe':
                      return (
                        <KiInternetFreigabeCard
                          key={key}
                          proposal={proposal}
                          onFreigegeben={anfrage =>
                            void handleSend(
                              `Freigegeben — bitte jetzt im Internet suchen: „${anfrage}“`
                            )
                          }
                        />
                      );
                    case 'internet_quellen':
                      // Steht schon an der Antwort selbst.
                      return null;
                    case 'kontrollstruktur_ablage':
                      return (
                        <KiKontrollstrukturAblageProposalCard
                          key={key}
                          proposal={proposal}
                        />
                      );
                    default:
                      return null;
                  }
                }
              )}

              {isPendingAskKi && (
                <Stack direction='row' spacing={1} alignItems='center'>
                  <KarlAvatar modus='denkt' />
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      borderRadius: 3,
                      borderTopLeftRadius: 6,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Denkpunkte />
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    Karl denkt nach …
                  </Typography>
                  <Button
                    size='small'
                    color='inherit'
                    startIcon={<Stop fontSize='small' />}
                    onClick={() => {
                      abortRef.current?.abort();

                      // Dem Server sagen, dass es vorbei ist. Er kann den
                      // Lauf beim Anbieter nicht beenden — bei watsonx
                      // Orchestrate geht das über die API nachweislich
                      // nicht —, aber er kann ihm den Schlüssel nehmen.
                      // Schlägt die Meldung fehl, ist es nicht schlimm: das
                      // Token läuft ohnehin nach zehn Minuten ab.
                      if (abbruchKennungRef.current !== null) {
                        void meldeAbbruch({
                          AbbruchKennung: abbruchKennungRef.current,
                        }).catch(() => undefined);
                      }
                    }}
                  >
                    Abbrechen
                  </Button>
                </Stack>
              )}
            </Stack>

            {error !== null && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Stack
            sx={{
              p: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
            spacing={1}
          >
            {attachment !== null && (
              <Chip
                size='small'
                icon={<AttachFile fontSize='small' />}
                label={`${attachment.FileName} — bleibt im Gespräch`}
                onDelete={() => {
                  setAttachment(null);
                  setIsAttachmentNew(false);
                }}
                sx={{ alignSelf: 'flex-start', maxWidth: '100%' }}
              />
            )}

            <Stack
              direction='row'
              spacing={0.5}
              alignItems='flex-end'
              sx={{
                pl: 0.5,
                pr: 0.75,
                py: 0.5,
                borderRadius: 4,
                border: 1,
                borderColor: 'divider',
                bgcolor: theme =>
                  theme.palette.mode === 'dark'
                    ? 'background.default'
                    : alpha(theme.palette.common.black, 0.02),
                transition: 'border-color 150ms ease, box-shadow 150ms ease',
                '&:focus-within': {
                  borderColor: 'primary.main',
                  boxShadow: theme =>
                    `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              }}
            >
              <input
                ref={fileInputRef}
                type='file'
                hidden
                accept={KI_ATTACHMENT_TYPES.join(',')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  void handleFiles(event.target.files);
                  // Zurücksetzen, sonst löst dieselbe Datei beim zweiten Mal
                  // kein change-Ereignis aus.
                  event.target.value = '';
                }}
              />
              <Tooltip title='Datei anhängen (PDF oder Bild)'>
                <span>
                  <IconButton
                    disabled={isPendingAskKi}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <AttachFile />
                  </IconButton>
                </span>
              </Tooltip>
              <InputBase
                inputRef={eingabeRef}
                value={input}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setInput(event.target.value)
                }
                onKeyDown={(event: React.KeyboardEvent) => {
                  // Enter sendet, Umschalt+Enter macht einen Absatz — wie in den
                  // übrigen Eingaben der Anwendung.
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
                onPaste={(event: React.ClipboardEvent) => {
                  // Ein Screenshot aus der Zwischenablage ist der schnellste
                  // Weg, etwas zu zeigen — ohne das müsste man ihn erst
                  // irgendwohin speichern.
                  if (event.clipboardData.files.length > 0) {
                    event.preventDefault();
                    void handleFiles(event.clipboardData.files);
                  }
                }}
                placeholder={
                  attachment !== null
                    ? 'Was soll mit der Datei geschehen?'
                    : 'Frage eingeben — Datei hineinziehen geht auch …'
                }
                fullWidth
                multiline
                maxRows={4}
                disabled={isPendingAskKi}
                sx={{ py: 0.9, fontSize: '0.95rem' }}
              />
              <IconButton
                disabled={isPendingAskKi || input.trim() === ''}
                onClick={() => void handleSend()}
                sx={{
                  mb: 0.25,
                  width: 38,
                  height: 38,
                  color: 'common.white',
                  background: verlauf,
                  transition: 'transform 120ms ease, opacity 120ms ease',
                  '&:hover': { transform: 'scale(1.06)' },
                  '&.Mui-disabled': {
                    background: 'none',
                    bgcolor: 'action.disabledBackground',
                    color: 'action.disabled',
                  },
                }}
              >
                <Send sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Stack>
        </Paper>
      )}

      <Tooltip title={isOpen ? 'Schließen' : 'Karl fragen'}>
        <Fab
          color='primary'
          onClick={() => setIsOpen(open => !open)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: theme => theme.zIndex.drawer + 2,
            background: verlauf,
            boxShadow: theme =>
              `0 8px 24px ${alpha(theme.palette.primary.main, 0.45)}`,
            transition: 'transform 150ms ease',
            '&:hover': { transform: 'scale(1.06)' },
          }}
        >
          {isOpen ? <Close /> : <KarlDino groesse={26} titel='Karl fragen' />}
        </Fab>
      </Tooltip>
    </KiVorgangContext.Provider>
  );
};
